import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IChatMessage } from 'src/interface/IChatState';
import { WindowContainer, InputContainer } from 'src/styled/Chat/ChatWindow.styled';
import { getContextualReplies } from 'src/utils/contextualReplies';
import { getPageContext } from 'src/utils/pageContextDetector';
import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import MessageList from './MessageList';
import QuickReplies from './QuickReplies';

interface IChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  messages: IChatMessage[];
  onSendMessage: (content: string) => void;
  loading?: boolean;
  typing?: boolean;
  websocketConnected?: boolean;
}

interface Position {
  x: number;
  y: number;
}

const STORAGE_KEY = 'chat-window-position';

export const ChatWindow: React.FC<IChatWindowProps> = ({
  isOpen,
  onClose,
  messages,
  onSendMessage,
  loading = false,
  typing = false,
  websocketConnected = false
}) => {
  const { i18n } = useTranslation();
  const windowRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<Position>(() => {
    // Load saved position from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved position:', e);
        }
      }
    }
    return { x: 24, y: 100 }; // Default position (right: 24px, bottom: 100px)
  });
  const dragStart = useRef<Position>({ x: 0, y: 0 });
  const positionStart = useRef<Position>({ x: 0, y: 0 });

  // Get contextual quick replies based on current page
  const pageContext = getPageContext();
  const contextualReplies = getContextualReplies(pageContext.section, i18n.language);

  // Save position to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    }
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const deltaX = dragStart.current.x - e.clientX;
    const deltaY = dragStart.current.y - e.clientY;

    const newX = Math.max(0, positionStart.current.x + deltaX);
    const newY = Math.max(0, positionStart.current.y + deltaY);

    // Prevent dragging outside viewport
    const maxX = window.innerWidth - (windowRef.current?.offsetWidth || 380);
    const maxY = window.innerHeight - (windowRef.current?.offsetHeight || 600);

    setPosition({
      x: Math.min(newX, maxX),
      y: Math.min(newY, maxY)
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging from header
    const target = e.target as HTMLElement;
    const dragHandle = target.closest('[data-drag-handle="true"]');

    if (dragHandle) {
      setIsDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY };
      positionStart.current = { ...position };
      e.preventDefault();
      e.stopPropagation();

      // Attach listeners immediately for instant response
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  return (
    <WindowContainer
      ref={windowRef}
      isOpen={isOpen}
      style={{
        right: `${position.x}px`,
        bottom: `${position.y}px`
      }}
      onMouseDown={handleMouseDown}>
      <ChatHeader
        onClose={onClose}
        isConnected={websocketConnected}
        isDraggable
        isDragging={isDragging}
      />
      <MessageList messages={messages} typing={typing} />
      <InputContainer>
        {messages.length === 0 && (
          <QuickReplies onSelect={onSendMessage} replies={contextualReplies} />
        )}
        <ChatInput onSend={onSendMessage} disabled={loading} />
      </InputContainer>
    </WindowContainer>
  );
};

export default ChatWindow;
